import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useMap } from 'react-map-gl'
import Supercluster from 'supercluster'
import {
  FIT_BOUNDS_ANIMATION_DURATION,
  FIT_BOUNDS_PADDING,
  SUPERCLUSTER_OPTIONS,
} from '../utils/constants/map.constants'
import { getBoundsFromCoords } from '../utils/helpers/map.helpers'
import XpSpotLayer from './XpSpotLayer'
import XpClusterLayer, {
  SuperclusterClusterProperties,
  SuperclusterFeature,
  SuperclusterFeatureProperties,
  SuperclusterPositionProperties,
} from './XpClusterLayer'
import { Position } from '../types/geoXp'
import { useXp } from '../contexts/XpContext'

const DEBOUNCE_UPDATE_TIME = 100

interface XpSpotsClusterProps {
  zoomOnClick?: boolean
  selectedPatterns: Position[]
}

const XpSpotsCluster = ({ zoomOnClick, selectedPatterns }: XpSpotsClusterProps) => {
  const { positions, active } = useXp()
  const { current: map } = useMap()

  const debounceUpdate = useRef<NodeJS.Timeout>()

  const [currentClusters, setCurrentClusters] = useState<
    Supercluster.PointFeature<SuperclusterFeatureProperties>[]
  >([])
  const [superCluster] = useState(
    new Supercluster({
      ...SUPERCLUSTER_OPTIONS,
      extent: 512,
    })
  )

  const activePositionsIds = useMemo(() => active?.map((audio) => audio?.spot?.position), [active])

  // load supercluster features
  const loadSuperCluster = useCallback(() => {
    const pointFeatures = positions.reduce((result: SuperclusterFeature[], pattern) => {
      if (selectedPatterns.map((p) => p.id).includes(pattern.id)) {
        return result.concat(
          pattern.positions?.map((position) => ({
            id: position.id,
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [position.lon, position.lat],
            },
            properties: {
              position,
              pattern,
            },
          })) ?? []
        )
      }

      return result
    }, [])

    superCluster.load(pointFeatures)
  }, [positions, selectedPatterns])

  // define update method
  const updateClusters = useCallback(() => {
    if (!map || !superCluster) return
    const zoom = map.getZoom()
    const bounds = map.getBounds()

    const updatedClusters = superCluster.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()], // [westLng, southLat, eastLng, northLat]
      zoom
    )

    setCurrentClusters(updatedClusters)
  }, [superCluster, currentClusters, positions])

  // on map moved
  const onMapChange = React.useCallback(() => {
    if (debounceUpdate.current) return
    debounceUpdate.current = setTimeout(() => {
      clearTimeout(debounceUpdate.current)
      debounceUpdate.current = undefined

      updateClusters()
    }, DEBOUNCE_UPDATE_TIME)
  }, [updateClusters])

  // fit map bounds to current cluster
  const fitClusterBounds = React.useCallback(
    (clusterId: number) => {
      if (!superCluster || !map || !clusterId) return
      const coords = superCluster
        ?.getLeaves(clusterId, Infinity)
        .map((leaf) => leaf.geometry.coordinates) as [number, number][]

      const mapBounds = getBoundsFromCoords(coords)
      map.fitBounds(mapBounds, {
        padding: FIT_BOUNDS_PADDING,
        duration: FIT_BOUNDS_ANIMATION_DURATION,
      })
    },
    [superCluster, map]
  )

  // update supercluster when map moves
  useEffect(() => {
    if (!map) return
    map.on('move', onMapChange)
    map.on('zoom', onMapChange)
    return () => {
      map?.off('move', onMapChange)
      map?.off('zoom', onMapChange)
    }
  }, [map, onMapChange])

  // load supercluster
  useEffect(() => {
    if (!superCluster) return

    loadSuperCluster()
    updateClusters()
  }, [positions, superCluster, selectedPatterns])

  return currentClusters?.map((feature) => {
    const { position, cluster } = feature.properties
    if (cluster) {
      const typedFeature = feature as Supercluster.PointFeature<SuperclusterClusterProperties>
      return (
        <XpClusterLayer
          key={typedFeature.id}
          feature={typedFeature}
          superCluster={superCluster}
          onClick={
            zoomOnClick
              ? (e) => {
                  fitClusterBounds(typedFeature.properties.cluster_id)
                  e.originalEvent.stopPropagation()
                }
              : undefined
          }
        />
      )
    }

    if (position) {
      const typedFeature = feature as Supercluster.PointFeature<SuperclusterPositionProperties>
      return (
        <XpSpotLayer
          key={typedFeature.id}
          feature={typedFeature}
          isActive={activePositionsIds.includes(position.id)}
        />
      )
    }

    return null
  })
}

export default XpSpotsCluster
